// src/components/layout/CasetaLayout.js
import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import Navbar from '../Navbar';
import { FaShieldAlt, FaSwimmingPool, FaCogs, FaBars, FaTimes, FaHome, FaBuilding } from 'react-icons/fa';
import { ResidentPendingProvider } from '../../context/ResidentPendingContext';
import { useAuth } from '../../context/AuthContext';
import { useModulos } from '../../hooks/useModulos';
import { db } from '../../firebase';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import SelectorFraccionamientoRapido from '../SelectorFraccionamientoRapido';
import './CasetaLayout.css';
import './CasetaSidebarMobile.css';
import Swal from 'sweetalert2';

const getInitials = (name) => {
    if (!name) return 'V';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
};

const CasetaLayout = () => {
    const { userData, fraccionamientoId: activeFraccId } = useAuth();
    const fraccionamientoId = activeFraccId || userData?.fraccionamientoId || userData?.fraccionamientoTd || null;

    // Escuchar alerta de mejoras reactiva global para caseta
    useEffect(() => {
        const configGlobalRef = doc(db, 'plataforma', 'config_global');
        const unsubscribe = onSnapshot(configGlobalRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                const esCaseta = ['caseta', 'vigilante'].includes(userData?.rol);
                const aplicaARol = data.rolesAlerta ? Boolean(data.rolesAlerta.caseta) : false;

                if (data.alertaMejorasActiva && data.alertaMejorasVersion && esCaseta && aplicaARol) {
                    const versionVista = localStorage.getItem('alerta_version_vista');
                    if (versionVista !== data.alertaMejorasVersion && !window.isAlertaMejorasActive) {
                        window.isAlertaMejorasActive = true;
                        Swal.fire({
                            title: data.alertaMejorasTitulo || '¡Actualización del Sistema!',
                            html: `<div style="text-align: left; font-size: 0.95rem; line-height: 1.5;">${data.alertaMejorasMensaje || 'Se han aplicado nuevas mejoras importantes al sistema. Es necesario recargar tu página para poder ver los cambios.'}</div>`,
                            icon: 'info',
                            confirmButtonText: 'Actualizar y Recargar',
                            confirmButtonColor: '#0284c7',
                            allowOutsideClick: false,
                            allowEscapeKey: false,
                            allowEnterKey: true
                        }).then((result) => {
                            if (result.isConfirmed) {
                                localStorage.setItem('alerta_version_vista', data.alertaMejorasVersion);
                                window.isAlertaMejorasActive = false;
                                window.location.reload(true);
                            }
                        });
                    }
                }
            }
        }, (err) => {
            console.warn('CasetaLayout: error escuchando config_global', err);
        });

        return () => unsubscribe();
    }, [userData]);

    const { modulos, loading } = useModulos(fraccionamientoId);
    const location = useLocation();
    const [sidebarVisible, setSidebarVisible] = useState(false);
    const checkIsMobile = () => window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const [isMobile, setIsMobile] = useState(checkIsMobile);
    const [fraccionamientoConfig, setFraccionamientoConfig] = useState({
        logoUrl: null,
        condominioNombre: null
    });

    // Detectar cambios en el tamaño de pantalla
    useEffect(() => {
        const handleResize = () => {
            const mobile = checkIsMobile();
            setIsMobile(mobile);
            // En móvil, ocultar sidebar por defecto
            if (mobile) {
                setSidebarVisible(false);
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Llamar una vez al montar

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Ocultar sidebar automáticamente cuando cambia la ruta (solo en móvil)
    useEffect(() => {
        if (isMobile) {
            setSidebarVisible(false);
        }
    }, [location.pathname, isMobile]);

    // Cargar configuración del fraccionamiento (logo y nombre)
    useEffect(() => {
        const loadFraccionamientoConfig = async () => {
            if (!fraccionamientoId) return;

            try {
                const fraccDoc = await getDoc(doc(db, 'fraccionamientos', fraccionamientoId));
                if (fraccDoc.exists()) {
                    const data = fraccDoc.data() || {};
                    const configRecibos = data.configuracionRecibos || {};

                    // Obtener logoUrl y condominioNombre desde configuracionRecibos o root-level
                    const logoUrl = configRecibos.logoUrl || data.logoUrl || null;
                    const condominioNombre = configRecibos.condominioNombre || data.condominioNombre || null;

                    setFraccionamientoConfig({
                        logoUrl,
                        condominioNombre
                    });
                }
            } catch (error) {
                console.error('Error al cargar configuración del fraccionamiento:', error);
            }
        };

        loadFraccionamientoConfig();
    }, [fraccionamientoId]);

    // Manejar botón de retroceso nativo de la app (solo en móvil)
    useEffect(() => {
        if (!isMobile) return;

        const isNative = Capacitor.isNativePlatform();
        let backButtonListener = null;

        if (isNative) {
            backButtonListener = App.addListener('backButton', ({ canGoBack }) => {
                if (sidebarVisible) {
                    // Si el sidebar está abierto, cerrarlo
                    setSidebarVisible(false);
                } else if (canGoBack) {
                    // Si el navegador puede ir hacia atrás, hacerlo
                    window.history.back();
                } else {
                    // Si no puede ir hacia atrás, salir de la app
                    App.exitApp();
                }
            });
        }

        return () => {
            if (backButtonListener) {
                backButtonListener.remove();
            }
        };
    }, [isMobile, sidebarVisible]);


    const casetaLinks = [
        // Vigilancia siempre visible para el rol Caseta
        { to: '/vigilancia', label: 'Vigilancia', icon: <FaShieldAlt /> },
        // Mostrar Amenidades solo si está habilitado o aún está cargando
        ...(loading || modulos.amenidades === true ? [{ to: '/amenidades', label: 'Amenidades', icon: <FaSwimmingPool /> }] : []),
        // Configuraciones siempre visible para el rol Caseta (Soporte, Perfil, etc)
        { to: '/configuraciones', label: 'Configuraciones', icon: <FaCogs /> },
    ];

    const toggleSidebar = () => {
        setSidebarVisible(!sidebarVisible);
    };

    return (
        <ResidentPendingProvider>
            <div className={`caseta-layout ${isMobile ? (sidebarVisible ? 'sidebar-visible' : 'sidebar-hidden') : 'desktop-mode'}`}>
                {/* Header con branding */}
                {/* Header con branding - Siempre visible */}
                <header className="caseta-header">
                    <div className="caseta-header-content">
                        <div className="caseta-branding" style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            {fraccionamientoConfig.logoUrl ? (
                                <img
                                    src={fraccionamientoConfig.logoUrl}
                                    alt={fraccionamientoConfig.condominioNombre || 'Logo'}
                                    className="caseta-brand-logo"
                                    style={{ height: '42px', maxWidth: '120px', objectFit: 'contain', borderRadius: '6px', background: '#ffffff', padding: '3px 6px', boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }}
                                />
                            ) : (
                                <FaHome className="caseta-brand-icon" />
                            )}
                            <div className="caseta-brand-text">
                                <h1>
                                    Portal de Caseta
                                    {(fraccionamientoConfig.condominioNombre || userData?.fraccionamientoNombre) && (
                                        <span className="caseta-brand-condo-name" style={{ opacity: 0.95, fontWeight: '700', marginLeft: '6px' }}>
                                            — {fraccionamientoConfig.condominioNombre || userData?.fraccionamientoNombre}
                                        </span>
                                    )}
                                </h1>
                                <p>Control de Accesos y Seguridad</p>
                            </div>
                        </div>
                        <div className="caseta-header-actions" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        </div>
                    </div>
                </header>

                {/* Navbar horizontal para desktop */}
                {!isMobile && (
                    <div className="caseta-navbar-wrapper">
                        <Navbar links={casetaLinks} />
                    </div>
                )}

                {/* Sidebar para móvil */}
                {isMobile && (
                    <aside className={`caseta-sidebar ${sidebarVisible ? 'visible' : 'hidden'}`}>
                        <div className="caseta-sidebar-header">
                            <div className="caseta-sidebar-profile-card">
                                <div className="caseta-sidebar-avatar-container">
                                    <div className="caseta-sidebar-avatar">
                                        {getInitials(userData?.nombre || userData?.nombreCompleto || 'Vigilancia')}
                                    </div>
                                    <span className="caseta-sidebar-status-dot" title="En línea" />
                                </div>
                                <div className="caseta-sidebar-user-details">
                                    <div className="caseta-sidebar-name-row">
                                        <span className="caseta-sidebar-user-name">{userData?.nombre || userData?.nombreCompleto || 'Vigilancia'}</span>
                                        <span className="caseta-sidebar-role-pill caseta">VIGILANCIA</span>
                                    </div>
                                    {(fraccionamientoConfig.condominioNombre || userData?.fraccionamientoNombre) && (
                                        <span className="caseta-sidebar-condo-badge">
                                            <FaBuilding style={{ fontSize: '0.75rem', flexShrink: 0 }} />
                                            <span className="condo-name-text">{fraccionamientoConfig.condominioNombre || userData?.fraccionamientoNombre}</span>
                                        </span>
                                    )}
                                </div>
                            </div>
                            <button
                                className="caseta-sidebar-close"
                                onClick={toggleSidebar}
                                aria-label="Cerrar menú"
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="caseta-sidebar-content">
                            <Navbar links={casetaLinks} isMobile={true} showSubmenus={false} />
                        </div>
                        <div className="caseta-sidebar-separator"></div>
                        <div className="caseta-sidebar-footer">
                            {fraccionamientoConfig.logoUrl ? (
                                <img src={fraccionamientoConfig.logoUrl} alt="Logo" className="caseta-sidebar-footer-logo" />
                            ) : (
                                <img src="/domus-logo.jpg" alt="Logo" className="caseta-sidebar-footer-logo" />
                            )}
                            <p className="caseta-sidebar-footer-name">{fraccionamientoConfig.condominioNombre || userData?.fraccionamientoNombre || 'Domus Habita'}</p>
                            <p className="caseta-sidebar-footer-copyright">© 2026 Domus Habita. Todos los derechos reservados</p>
                        </div>
                    </aside>
                )}

                <main className="caseta-content">
                    <Outlet />
                </main>

                {/* Botón flotante visible en móvil solo cuando el sidebar está oculto */}
                {isMobile && !sidebarVisible && (
                    <button
                        className="sidebar-float-btn"
                        onClick={toggleSidebar}
                        aria-label="Abrir menú"
                        title="Abrir menú"
                    >
                        <FaBars />
                    </button>
                )}

                {/* Overlay para cerrar el sidebar al hacer clic fuera en móviles */}
                {isMobile && sidebarVisible && (
                    <div
                        className="sidebar-overlay"
                        onClick={toggleSidebar}
                        onTouchStart={toggleSidebar}
                        aria-hidden="true"
                    />
                )}
            </div>
        </ResidentPendingProvider >
    );
};

export default CasetaLayout;
